import { readItems } from '@directus/sdk';
import { useQuery } from '@tanstack/react-query';

import type { IncompleteEvent } from '../../../../types/DirectusSchema';
import { directusClient } from '../../services/directusClient/directusClient.ts';
import { parseIncompleteEvent } from '../lib/parseIncompleteEvent.ts';
import { usePromoterItemQuery } from '../promoter/usePromoterItemQuery.ts';

type SortOption = 'start' | '-start' | 'name' | '-name' | 'date_created' | '-date_created';

interface IncompleteEventsQueryParams {
  page: number;
  limit: number;
  sort: SortOption;
  categoryId?: number;
  eventType?: 'online' | 'live' | 'secret';
  enabled?: boolean;
}

export const usePaginatedIncompleteEventsQuery = ({
  page,
  limit,
  sort,
  categoryId,
  eventType,
  enabled,
}: IncompleteEventsQueryParams) => {
  const { data } = usePromoterItemQuery();

  return useQuery({
    queryKey: ['incomplete_events', 'paginated', { page, limit, sort, categoryId, eventType, promoterId: data?.id }],
    queryFn: async () => {
      const filter: Record<string, unknown> = {
        promoter: { _eq: data?.id },
      };

      if (categoryId) {
        filter.category = { _eq: categoryId };
      }

      if (eventType) {
        filter.type = { _eq: eventType };
      }

      const result = await directusClient.request(
        readItems('incomplete_events', {
          filter,
          fields: [
            '*',
            {
              venue: ['id', 'commercial_name'],
              category: ['id', 'title'],
              target_audiences: ['id', { target_audiences_id: ['*'] }],
            },
          ],
          sort: [sort],
          limit,
          offset: (page - 1) * limit,
        }),
      );

      return result.map((e) => parseIncompleteEvent(e as unknown as IncompleteEvent));
    },
    enabled: enabled !== false && !!data?.id,
  });
};
